import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import BarChartIcon from "@mui/icons-material/BarChart";
import CommentIcon from "@mui/icons-material/Comment";
import PeopleIcon from "@mui/icons-material/People";
import VisibilityIcon from "@mui/icons-material/Visibility";
import dayjs from "dayjs";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, Tooltip,
  XAxis, YAxis, ResponsiveContainer, Legend,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import { AnalyticsCard } from "../components/AnalyticsCard";

import { fetchOverview, fetchPostAnalytics, clearPostAnalytics } from "../analyticsSlice";
import { fetchPosts } from "../blogSlice";
import { inputSx } from "../blogTheme";

const PIE_COLORS = ["#065F46", "#f59e0b", "#94a3b8", "#EA001B"];

export default function AnalyticsDashboard() {
  const dispatch = useDispatch();
  const { overview, postAnalytics, loading, error } = useSelector((state) => state.analytics);
  const { posts } = useSelector((state) => state.blog);
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    dispatch(fetchOverview());
    dispatch(fetchPosts({ limit: 100 }));
    return () => { dispatch(clearPostAnalytics()); };
  }, [dispatch]);

  useEffect(() => {
    if (selectedPost) dispatch(fetchPostAnalytics(selectedPost._id));
    else              dispatch(clearPostAnalytics());
  }, [selectedPost]);

  const deviceData = postAnalytics?.devices
    ? [
        { name: "Mobile",  value: postAnalytics.devices.mobile  || 0 },
        { name: "Tablet",  value: postAnalytics.devices.tablet  || 0 },
        { name: "Desktop", value: postAnalytics.devices.desktop || 0 },
      ]
    : [];

  return (
    <Box sx={{ px: 3, pt: 1.5, pb: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: "#362B24", fontFamily: "Heebo", mb: 2 }}>Analytics</Typography>

      {loading && <CircularProgress size={24} sx={{ mb: 2, color: "#065F46" }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <AnalyticsCard title="Total Views"    value={overview?.totalViews    ?? "—"} icon={VisibilityIcon} />
        </Grid>
        <Grid item xs={6} md={3}>
          <AnalyticsCard title="Total Posts"    value={overview?.totalPosts    ?? "—"} icon={BarChartIcon} />
        </Grid>
        <Grid item xs={6} md={3}>
          <AnalyticsCard title="Total Comments" value={overview?.totalComments ?? "—"} icon={CommentIcon} />
        </Grid>
        <Grid item xs={6} md={3}>
          <AnalyticsCard title="Top Post Views" value={overview?.topPosts?.[0]?.views ?? "—"} icon={PeopleIcon} />
        </Grid>
      </Grid>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: "24px", boxShadow: "3px 2px 20px 0px rgba(0,0,0,0.04)" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#362B24", fontFamily: "Heebo", mb: 1 }}>
              Daily Views (last 30 days)
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={overview?.dailyViews || []}>
                <defs>
                  <linearGradient id="viewGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#065F46" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#065F46" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tickFormatter={(d) => dayjs(d).format("MMM D")} tick={{ fontSize: 11, fill: "#362B24" }} />
                <YAxis tick={{ fontSize: 11, fill: "#362B24" }} />
                <Tooltip labelFormatter={(d) => dayjs(d).format("MMM D, YYYY")} />
                <Area type="monotone" dataKey="views" stroke="#065F46" fill="url(#viewGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: "24px", boxShadow: "3px 2px 20px 0px rgba(0,0,0,0.04)" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#362B24", fontFamily: "Heebo", mb: 1 }}>
              Top 10 Posts by Views
            </Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={(overview?.topPosts || []).slice(0, 10)} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 11, fill: "#362B24" }} />
                <YAxis type="category" dataKey="title" width={180} tick={{ fontSize: 11, fill: "#362B24" }} />
                <Tooltip />
                <Bar dataKey="views" fill="#065F46" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#362B24", fontFamily: "Heebo", mb: 1 }}>
          Per-Post Analytics
        </Typography>
        <Autocomplete
          options={posts}
          getOptionLabel={(p) => p.title || ""}
          value={selectedPost}
          onChange={(_, val) => setSelectedPost(val)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select a post"
              size="small"
              sx={{ maxWidth: 400, ...inputSx }}
            />
          )}
        />
      </Box>

      {selectedPost && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: "24px", boxShadow: "3px 2px 20px 0px rgba(0,0,0,0.04)" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#362B24", fontFamily: "Heebo", mb: 1 }}>
                Device Breakdown
              </Typography>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={deviceData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {deviceData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: "24px", boxShadow: "3px 2px 20px 0px rgba(0,0,0,0.04)" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#362B24", fontFamily: "Heebo", mb: 1 }}>
                Top 10 Referrers
              </Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={(postAnalytics?.referrers || []).slice(0, 10)}>
                  <XAxis dataKey="source" tick={{ fontSize: 10, fill: "#362B24" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#362B24" }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0D7B5C" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {!selectedPost && (
        <Typography sx={{ color: "#362B24", opacity: 0.5, fontFamily: "Heebo" }} variant="body2">
          Select a post above to view device breakdown and referrer data.
        </Typography>
      )}
    </Box>
  );
}
